/**
 * Deleting ONE run of a seam (#1030, epic #781).
 *
 * Construction's Delete has had segment / anchor / handle / prune / sweep for a
 * while, and #116 added "drop one stretch of an offset". The leather side had
 * nothing equivalent: `pickLeatherObjectAt` resolves a cursor to a whole part,
 * cut-out or seam, and a tap removed all of it.
 *
 * A seam is a baked `points: Vec2[]`, so this needs no link back to whatever
 * produced it — which matters, because an offset seam (#768) keeps no record of
 * the part edge it came from and could never "un-follow" an edge the way a
 * construction offset can (#116). It doesn't need to: removing a run is a
 * polyline edit.
 *
 * The geometry lines up unusually well too. A seam's points come off its source
 * per EDGE — a straight edge contributes only its two endpoints — so a seam
 * inset in a rectangular part is four points and "the run under my cursor" is
 * exactly one edge of the part. There is no ambiguity about what a tap means.
 *
 * Deliberately NOT here: the same gesture on a part outline or a cut-out edge.
 * Both are closed regions where removing an edge means something else entirely
 * (an open part outline is not a part), so they want their own thinking.
 */
import type paper from "paper";
import type { SeamHandle } from "./seamGeometry";
import {
  seamPaperPath,
  seamRunCount,
  seamRunPathData,
  normalizeSeamHandles,
} from "./seamGeometry";
import type { Vec2 } from "./Project";

/** The shape of a seam this module needs — anchors, optional curves, and
 *  whether it loops. Deliberately structural: the targeting is also used
 *  against a seam shifted into display space, which is not a `StitchLine`. */
export interface SeamShape {
  points: Vec2[];
  handles?: SeamHandle[];
  closed?: boolean;
}

/** A run of a seam, picked by a cursor. `pathData` is that run alone, ready to
 *  hand to an overlay — the same ready-made-geometry shape construction's
 *  `kind: "offset-segment"` hover spec uses, rather than a pair of indices the
 *  renderer would have to resolve again. */
export interface SeamRunPick {
  index: number;
  pathData: string;
  /** Distance (mm) from the cursor to the run. */
  distance: number;
}

/** Which run of `seam` is nearest `point`, or null past `tolMm` / on a seam
 *  with no runs at all. */
export function nearestSeamRun(
  seam: SeamShape,
  point: Vec2,
  scope: paper.PaperScope,
  tolMm: number,
): SeamRunPick | null {
  if (seamRunCount(seam) === 0) return null;
  const path = seamPaperPath(seam, scope);
  try {
    const at = new scope.Point(point.x, point.y);
    const loc = path.getNearestLocation(at);
    if (!loc) return null;
    const distance = loc.point.getDistance(at);
    if (distance > tolMm) return null;
    // Paper numbers a path's curves the way this module numbers its runs:
    // curve `i` joins segment `i` to the one after it, and a closed path has
    // the extra curve back to the start.
    const index = loc.curve?.index ?? -1;
    if (index < 0 || index >= seamRunCount(seam)) return null;
    const pathData = seamRunPathData(seam, index);
    return pathData ? { index, pathData, distance } : null;
  } finally {
    path.remove();
  }
}

/** Handles for a slice of a seam's anchors, in the order `order` gives them,
 *  with the two ends' outward-facing controls cleared.
 *
 *  Clearing them matters: run `i → i+1` is drawn by anchor `i`'s `out` and
 *  anchor `i+1`'s `in`, so when that run goes those two controls belong to
 *  nothing. Left in place they'd be invisible until something re-closed or
 *  re-joined the seam, and then a curve nobody asked for would reappear. */
function slicedHandles(
  handles: SeamHandle[] | undefined,
  order: number[],
): SeamHandle[] | undefined {
  if (!handles || order.length === 0) return undefined;
  const out = order.map((i) => ({
    in: handles[i]?.in ?? { x: 0, y: 0 },
    out: handles[i]?.out ?? { x: 0, y: 0 },
  }));
  out[0] = { in: { x: 0, y: 0 }, out: out[0]!.out };
  out[out.length - 1] = { in: out[out.length - 1]!.in, out: { x: 0, y: 0 } };
  return normalizeSeamHandles(out);
}

/**
 * What is left of a seam once run `index` is cut out — zero, one or two seams.
 *
 * - A CLOSED seam opens: the ring is re-cut to start just after the gap and end
 *   just before it, so every anchor survives and only the run between them
 *   goes.
 * - An OPEN seam cut at either end simply loses that end's anchor.
 * - An OPEN seam cut in the middle becomes TWO seams. Construction's segment
 *   delete already produces several lines from one, so the shape of that is
 *   established; here the caller gives the second one its own id and name.
 * - A side left with a single anchor isn't a seam and is dropped, so cutting
 *   the only run of a two-point seam returns nothing — i.e. the seam goes.
 */
export function seamsAfterRunDelete(
  seam: SeamShape,
  index: number,
): Array<{ points: Vec2[]; handles?: SeamHandle[]; closed: false }> {
  const n = seam.points.length;
  if (index < 0 || index >= seamRunCount(seam)) return [];
  const build = (order: number[]) =>
    order.length >= 2
      ? [
          {
            points: order.map((i) => seam.points[i]!),
            handles: slicedHandles(seam.handles, order),
            closed: false as const,
          },
        ]
      : [];

  if (seam.closed) {
    // Rotate the ring so it starts after the gap: index+1, index+2, … index.
    const order: number[] = [];
    for (let k = 1; k <= n; k++) order.push((index + k) % n);
    return build(order);
  }
  const head: number[] = [];
  for (let i = 0; i <= index; i++) head.push(i);
  const tail: number[] = [];
  for (let i = index + 1; i < n; i++) tail.push(i);
  return [...build(head), ...build(tail)];
}
